import { Inventory, IVehicleProperty } from "@/DataPages/types/inventory/models/inventory";
import { InventoryTab } from "../constants";
import { useMemo, useState } from "react";
import { getLogger } from "@/Common/helpers/getLogger";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import {
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridActionsCellItem,
	GridCellParams,
	GridColDef,
	GridColumnHeaderParams,
	GridRenderCellParams,
	GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { isFooterCell } from "@/Washouts/helpers/tables";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { Tooltip } from "@mui/material";

// Icons
import { ReactComponent as AttentionIcon } from "@/assets/icons/dataPages/inventory/information.svg";
import { ReactComponent as GPSIcon } from "@/assets/icons/dataPages/inventory/gps.svg";
import { COLUMN_RESIZE_FACTOR } from "@/Constants/tables";
import { usePersistentSelection } from "@/Common/hooks/selection/usePersistentSelection";
import clsx from "clsx";
import { SelectionAndFilterCheckbox } from "../SelectionAndFilterCheckbox";
import { UrgentActionsGridCell } from "../UrgentActionsGridCell";
import { useInventoryContext } from "../contexts/InventoryFilterContext";
import { SelectionAndFilterCheckboxAll } from "../SelectionAndFilterAllCheckbox";

const log = getLogger("useInventoryTableColumns");

// Constants
const MAX_COLUMNS_WITHOUT_SCROLL = 12;

/******************************************* HELPERS *******************************************/
const getClassNames = () => {
	return clsx({
		"group-[.odd-row]/stripe:!bg-[#F7F7F7]": true,
		"group-[.even-row]/stripe:!bg-white": true,
		"group-[.table-footer]/footer:!bg-[#2357BE] ": true,
	});
};

function buildBasicColumn(hints?: { isMoney: boolean }): GridColDef {
	const { isMoney = false } = hints ?? {};
	return {
		cellClassName: getCellClassNameFn([], {}, {}),
		renderHeader: (params: GridColumnHeaderParams) => <TypographyHeader params={params} />,
		renderCell: (params: GridRenderCellParams) => {
			if (isFooterCell(params)) {
				return <TypographyCell />;
			}
			if (isMoney) {
				return <TypographyCell params={params} render={"absolute"} hints={{ isMoney: true }} />;
			} else {
				return <TypographyCell params={params} />;
			}
		},
	} as unknown as GridColDef;
}

function buildBasicPinnedColumn(): GridColDef {
	return {
		cellClassName: (params: GridCellParams) => {
			if (isFooterCell(params)) {
				return `${getClassNames()} footer`;
			}
			return getClassNames();
		},
		renderHeader: (params: GridColumnHeaderParams) => <TypographyHeader params={params} />,
		renderCell: (params: GridRenderCellParams) => {
			if (isFooterCell(params)) {
				return <TypographyCell params={params} className={`${getClassNames()} footer`} />;
			}
			return <TypographyCell params={params} className={`${getClassNames()} pinned`} />;
		},
	} as unknown as GridColDef;
}
function toCamelCase(str: string): string {
	str = str.toLowerCase();
	return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
		if (+match === 0) {
			return "";
		}
		return index === 0 ? match.toLowerCase() : match.toUpperCase();
	});
}

const getGpsIcon = (
	params: GridCellParams<GridValidRowModel>,
	onShowDetailedInformation: (
		from: "isVehicleDetailedInformationDialogOpen" | "isGPSInformationDialogOpen",
		action: "open" | "close",
		vin?: string
	) => void
) => {
	let gpsClassName = "fill-gray-text";
	if (!(params.row.gpsId && params.row.gpsProtected)) {
		gpsClassName = "fill-disabled";
	}
	if (params.row.gpsInconsistency) {
		gpsClassName = `fill-error`;
	}

	const onClickGpsIcon = () => {
		onShowDetailedInformation("isGPSInformationDialogOpen", "open", params.row.vin as string);
	};

	if (params.row.gpsInconsistency === "LOCATION_CONFLICT") {
		return (
			<Tooltip
				title={"Device registered under sister store"}
				key="key"
				arrow
				placement="right-end"
				classes={{ tooltip: "!font-regular-sans" }}
				slotProps={{
					popper: {
						modifiers: [
							{
								name: "offset",
								options: {
									offset: [0, -10],
								},
							},
						],
					},
				}}
			>
				<GridActionsCellItem
					key="location"
					icon={<GPSIcon className={gpsClassName} />}
					label="Location"
					className="textPrimary"
					color="inherit"
					disabled={!(params.row.gpsId && params.row.gpsProtected)}
					onClick={onClickGpsIcon}
				/>
			</Tooltip>
		);
	}
	return (
		<GridActionsCellItem
			key="location"
			icon={<GPSIcon className={gpsClassName} />}
			label="Location"
			className="textPrimary"
			color="inherit"
			disabled={!(params.row.gpsId && params.row.gpsProtected)}
			onClick={onClickGpsIcon}
		/>
	);
};

const strictColumns = [
	"_id",
	"dmsId",
	"gpsId",
	"kmsId",
	"gpsInconsistency",
	"gpsProtected",
	"stockId",
	"locationId",
	"systemStatus",
	"statusInHistory",
	"vin",
	"properties",
	"keyStates",
	"kmsLastEventsByKiosk",
];

const buildColumns = (row: Inventory, properties: IVehicleProperty[], columnsWidth: number): GridColDef[] => {
	return Object.keys(row)
		?.filter((key) => !strictColumns.includes(key))
		.map((key) => {
			const property = properties.find((prop) => toCamelCase(prop.displayName) === key);
			return {
				...buildBasicColumn(),
				field: key,
				headerName: property?.displayName,
				type: property?.dataType,
				resizable: columnsWidth > 0,
				width: columnsWidth > 0 ? columnsWidth : undefined,
				minWidth: columnsWidth > 0 ? columnsWidth : undefined,
				maxWidth: columnsWidth > 0 ? columnsWidth * COLUMN_RESIZE_FACTOR : undefined,
				flex: columnsWidth ? 0 : 1,
				cellClassName: (params) => {
					if (isFooterCell(params)) {
						return `${getClassNames()} footer`;
					}
					return ``;
				},
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => {
					if (isFooterCell(params)) {
						return <TypographyCell className="!absolute !bottom-0" />;
					}
					return <TypographyCell params={params} />;
				},
			};
		}) as GridColDef[];
};

interface IUseInventoryTableColumnsArgs {
	tab: InventoryTab;
	rows: Inventory[];
	onShowDetailedInformation: (
		from: "isVehicleDetailedInformationDialogOpen" | "isGPSInformationDialogOpen",
		action: "open" | "close",
		vin?: string | undefined
	) => void;
}

export const useInventoryTableColumns = (args: IUseInventoryTableColumnsArgs): GridColDef[] => {
	const { rows, onShowDetailedInformation, tab } = args;

	const [builtColumns, setBuiltColumns] = useState<GridColDef[]>([]);

	if (!builtColumns.length && rows.length) {
		log("Building initial columns");
		const columnsWidth = rows?.[0]?.properties?.length < MAX_COLUMNS_WITHOUT_SCROLL ? 0 : 100;
		setBuiltColumns(buildColumns(rows?.[0], rows?.[0]?.properties, columnsWidth));
	}

	const { commissionCycleId, payPeriodId } = useInventoryContext();

	const { selectedIds: selectedRowIds } = usePersistentSelection({ table: tab });

	const pinnedColumns = useMemo(
		() =>
			[
				{
					field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
					width: 50,
					filterable: false,
					sortable: false,
					resizable: false,
					cellClassName: (params) => {
						if (isFooterCell(params)) {
							return `${getClassNames()} footer`;
						}
						return getClassNames();
					},
					colSpan: (_, other) => {
						const colSpan = 3;
						return other?._id ? 1 : colSpan;
					},
					renderHeader: () => <SelectionAndFilterCheckboxAll table={tab} data={rows} origin={tab} pathToVinNumber="vin" />,
					renderCell: (params) => {
						if (isFooterCell(params)) {
							return <p className={"!col-span-3 !text-[14px] font-montserrat footer"}>Total Assets</p>;
						}

						return (
							<SelectionAndFilterCheckbox
								_id={params.row._id as string}
								table={tab}
								row={params.row as Inventory}
								origin={tab}
								pathToVinNumber="vin"
								className="!ml-[-10px]"
							/>
						);
					},
				},
				{
					field: "DMS",
					headerName: "",
					width: 25,
					resizable: false,
					type: "singleSelect",
					valueOptions: ["Available", "Unavailable"],
					valueGetter: (_value, row) => (row.dmsId ? "Available" : "Unavailable"),
					cellClassName: (params) => {
						if (isFooterCell(params)) {
							return `${getClassNames()} footer`;
						}
						return `${getClassNames()} !p-0`;
					},
					renderCell: (params) => {
						if (isFooterCell(params)) {
							return <></>;
						}
						return (
							<GridActionsCellItem
								key="attention"
								icon={<AttentionIcon className={!params.row.dmsId ? "fill-disabled" : "fill-gray-text"} />}
								label="Attention"
								className="textPrimary "
								color="inherit"
								disabled={!params.row.dmsId}
								onClick={() =>
									onShowDetailedInformation("isVehicleDetailedInformationDialogOpen", "open", params.row.vin as string)
								}
							/>
						);
					},
				},
				{
					field: "GPS",
					headerName: "",
					width: 25,
					resizable: false,
					type: "singleSelect",
					valueOptions: ["Available", "Unavailable"],
					cellClassName: (params) => {
						if (isFooterCell(params)) {
							return `${getClassNames()} footer`;
						}
						return `${getClassNames()} !p-0`;
					},
					valueGetter: (_value, row) => (row.gpsId && row.gpsProtected ? "Available" : "Unavailable"),
					renderCell: (params) => {
						if (isFooterCell(params)) {
							return <></>;
						}
						return getGpsIcon(params, onShowDetailedInformation);
					},
				},
				{
					field: "KMS",
					headerName: "",
					width: 25,
					resizable: false,
					type: "singleSelect",
					valueOptions: ["Available", "Unavailable"],
					valueGetter: (_value, row) => (row.kmsId ? "Available" : "Unavailable"),
					cellClassName: (params) => {
						if (isFooterCell(params)) {
							return `${getClassNames()} footer`;
						}
						return `${getClassNames()} !p-0`;
					},
					renderCell: (params) => {
						if (isFooterCell(params)) {
							return <></>;
						}
						return (
							<UrgentActionsGridCell
								keyStates={params.row?.keyStates}
								kmsLastEventsByKiosk={params.row?.kmsLastEventsByKiosk}
							/>
						);
					},
				},
				{ field: "_id", headerName: "ID", filterable: false, pinnable: true, type: "string" },
				{
					...buildBasicPinnedColumn(),
					field: "stockId",
					headerName: "Stock #",
					type: "string",
					width: 90,
					headerAlign: "left",
					align: "left",
					resizable: false,
				},
				{
					...buildBasicPinnedColumn(),
					field: "vin",
					headerName: "VIN",
					type: "string",
					width: 130,
					resizable: false,
				},
				{
					...buildBasicColumn(),
					field: "statusInHistory",
					headerName: "Status",
					type: "string",
					width: 125,
					resizable: false,
					cellClassName: (params) => {
						if (isFooterCell(params)) {
							return `${getClassNames()} footer`;
						}
						return "";
					},
				},
				{
					field: "empty",
					headerName: "",
					sortable: false,
					resizable: false,
					disableReorder: true,
					width: 20,
					minWidth: 20,
					maxWidth: 20,
					headerClassName: "!z-10 !bg-white",
					cellClassName: () => "empty-row !bg-white",
					renderCell: () => <TypographyCell className={"!bg-blue"} />,
				},
			] as GridColDef[],
		[selectedRowIds, onShowDetailedInformation, commissionCycleId, payPeriodId]
	);

	return useMemo(() => [...pinnedColumns, ...builtColumns], [pinnedColumns, builtColumns]);
};
